import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, RotateCcw } from 'lucide-react';

import { useQueryClient } from '@tanstack/react-query';
import Button from '@components/common/Button';
import { Input } from '@components/common';
import SettingsSectionHeader from '@components/features/data-management-settings/SettingsSectionHeader';
import { useReportDirty } from '@components/features/data-management-settings/useReportDirty';
import type { SettingsPaneProps } from '@components/features/data-management-settings/settingsNav';
import type { SupplierTierAdmin } from '@app-types/supplier-tier-admin';
import { supplierTierAdminService } from '@services';
import { getApiErrorMessage } from '@utils/apiError';
import { REFERENCE_CATALOG_QUERY_KEY, SUPPLIER_TIERS_QUERY_KEY } from '@utils/referenceCatalog';
import { showToast } from '@utils/toast';
import styles from './ReferenceListsSettings.module.css';

interface DraftTier {
  id: number;
  name: string;
  description: string;
}

function compareByCode(left: string, right: string): number {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

function sortTiersByCode(tiers: SupplierTierAdmin[]): SupplierTierAdmin[] {
  return [...tiers].sort((left, right) => compareByCode(left.code, right.code));
}

function toDrafts(tiers: SupplierTierAdmin[]): DraftTier[] {
  return tiers.map((tier) => ({ id: tier.id, name: tier.name, description: tier.description ?? '' }));
}

function isDirty(drafts: DraftTier[], tiers: SupplierTierAdmin[]): boolean {
  return drafts.some((draft, index) => {
    const tier = tiers[index];
    return draft.name !== tier.name || draft.description !== (tier.description ?? '');
  });
}

const TierLabelsSettings: React.FC<SettingsPaneProps> = ({ onDirtyChange }) => {
  const queryClient = useQueryClient();
  const [tiers, setTiers] = useState<SupplierTierAdmin[]>([]);
  const [drafts, setDrafts] = useState<DraftTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTiers = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = sortTiersByCode(await supplierTierAdminService.getAll());
      setTiers(data);
      setDrafts(toDrafts(data));
    } catch {
      setLoadError(true);
      showToast.error('Failed to load tiers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTiers();
  }, [loadTiers]);

  const dirty = useMemo(() => isDirty(drafts, tiers), [drafts, tiers]);
  useReportDirty(dirty, onDirtyChange);

  const handleDraftChange = (index: number, patch: Partial<DraftTier>) => {
    setDrafts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleReset = () => {
    setDrafts(toDrafts(tiers));
  };

  const handleSave = async () => {
    const trimmedNames = drafts.map((draft) => draft.name.trim());
    if (trimmedNames.some((name) => !name)) {
      showToast.error('All tiers must have a name');
      return;
    }

    const seen = new Set<string>();
    for (const name of trimmedNames) {
      if (seen.has(name)) {
        showToast.error(`Tier names must be unique. "${name}" is used more than once.`);
        return;
      }
      seen.add(name);
    }

    try {
      setSaving(true);
      const updatedTiers: SupplierTierAdmin[] = [...tiers];

      for (let index = 0; index < drafts.length; index += 1) {
        const nextName = trimmedNames[index];
        const nextDescription = drafts[index].description.trim() === '' ? null : drafts[index].description.trim();
        const current = tiers[index];
        if (nextName !== current.name || nextDescription !== current.description) {
          const updated = await supplierTierAdminService.update(drafts[index].id, {
            name: nextName,
            description: nextDescription,
          });
          updatedTiers[index] = updated;
        }
      }

      setTiers(updatedTiers);
      setDrafts(toDrafts(updatedTiers));
      await queryClient.invalidateQueries({ queryKey: SUPPLIER_TIERS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: REFERENCE_CATALOG_QUERY_KEY });
      showToast.success('Supplier tiers saved');
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Failed to save supplier tiers'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading} data-test-id="tier-labels-loading">
        <Loader2 size={48} className="spin" />
        <p>Loading tiers...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.error} data-test-id="tier-labels-error">
        Failed to load tiers
      </div>
    );
  }

  return (
    <div className={styles.container} data-test-id="tier-labels-settings">
      <SettingsSectionHeader
        sticky
        title="Supplier Tiers"
        description="Edit display names and descriptions for supplier tier classifications. The code stays the same."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={!dirty || saving}
              dataTestId="tier-labels-reset"
            >
              <RotateCcw size={14} />
              Reset
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSave()}
              disabled={!dirty || saving}
              loading={saving}
              loadingText="Saving..."
              dataTestId="tier-labels-save"
            >
              <Save size={14} />
              Save
            </Button>
          </>
        }
      />

      {tiers.length === 0 ? (
        <div className={styles.empty} data-test-id="tier-labels-empty">
          No tiers are configured.
        </div>
      ) : (
        <div className={styles.valueList} data-test-id="tier-labels-card">
          <div className={styles.tierHeader}>
            <span />
            <span>Code</span>
            <span>Name</span>
            <span>Description</span>
          </div>
          {drafts.map((tier, index) => (
            <div key={tier.id} className={styles.tierRow}>
              <span className={styles.valueIndex}>{index + 1}</span>
              <span className={styles.codeLabel}>{tiers[index].code}</span>
              <Input
                placeholder="Tier name"
                value={tier.name}
                maxLength={255}
                onChange={(event) => handleDraftChange(index, { name: event.target.value })}
                dataTestId={`tier-name-${tier.id}`}
              />
              <Input
                placeholder="Description"
                value={tier.description}
                maxLength={1000}
                onChange={(event) => handleDraftChange(index, { description: event.target.value })}
                dataTestId={`tier-description-${tier.id}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TierLabelsSettings;
